// @ts-nocheck
import { platformApi } from '@/lib/platform-api';
import type {
  Payout,
  PayoutDetail,
  PayoutEvent,
  PayoutItem,
  PayoutL2Assignment,
  PayoutStatus,
  PayoutsFilters,
  PayoutsResponse,
  UnmatchedPayout,
  UnmatchedPayoutsListResponse,
} from '../types/payouts';

function mapPayoutResponse(r: Record<string, unknown>): Payout {
  return {
    id: r.id as string,
    operator_id: (r.operator_id as string) ?? '',
    client_id: r.client_id as string,
    amount_millicents: Number(r.amount_millicents),
    currency: (r.currency as string) ?? 'EUR',
    status: r.status as PayoutStatus,
    atlar_payment_id: (r.atlar_payment_id as string) ?? null,
    atlar_approval_step_id: (r.atlar_approval_step_id as string) ?? null,
    atlar_created_by_user_id: (r.atlar_created_by_user_id as string) ?? null,
    atlar_version: Number(r.atlar_version ?? 0),
    send_requested_at: (r.send_requested_at as string) ?? null,
    send_requested_by_user_id: (r.send_requested_by_user_id as string) ?? null,
    approve_requested_at: (r.approve_requested_at as string) ?? null,
    approve_requested_by_user_id: (r.approve_requested_by_user_id as string) ?? null,
    settled_at: (r.settled_at as string) ?? null,
    reversed_at: (r.reversed_at as string) ?? null,
    batch_reference: (r.batch_reference as string) ?? null,
    sent_at: (r.sent_at as string) ?? null,
    confirmed_at: (r.confirmed_at as string) ?? null,
    rejection_reason: (r.rejection_reason as string) ?? null,
    cancelled_at: (r.cancelled_at as string) ?? null,
    cancelled_reason: (r.cancelled_reason as Payout['cancelled_reason']) ?? null,
    period_start: (r.period_start as string) ?? '',
    period_end: (r.period_end as string) ?? '',
    bank_statement_reference: (r.bank_statement_reference as string) ?? null,
    destination_account_masked: (r.destination_account_masked as string) ?? null,
    destination_account_holder_name: (r.destination_account_holder_name as string) ?? null,
    bank_statement_line_id: (r.bank_statement_line_id as string) ?? null,
    l1_approved_by_user_id: (r.l1_approved_by_user_id as string) ?? null,
    l2_override_used: (r.l2_override_used as boolean) ?? false,
    l2_override_reason: (r.l2_override_reason as string) ?? null,
    metadata: (r.metadata as Record<string, unknown>) ?? {},
    created_at: (r.created_at as string) ?? '',
    updated_at: (r.updated_at as string) ?? '',
    client_name: ((r.client as Record<string, unknown>)?.display_name as string) ?? undefined,
  };
}

interface FetchPayoutsOptions {
  filters?: PayoutsFilters;
}

export async function fetchPayouts(options: FetchPayoutsOptions = {}): Promise<PayoutsResponse> {
  const { filters = {} } = options;
  const limit = filters.pageSize || 20;

  const { data, error } = await platformApi.GET('/platform/v1/finance/payouts', {
    params: {
      query: {
        limit,
        cursor: filters.cursor ?? undefined,
        status: filters.status ?? undefined,
        client_id: filters.clientId ?? undefined,
        sort: '-created_at',
        'expand[]': ['client'],
      },
    },
  });

  if (error || !data) {
    throw new Error('Failed to fetch payouts');
  }

  return {
    payouts: data.data.map((r: any) => mapPayoutResponse(r as unknown as Record<string, unknown>)),
    hasMore: data.has_more,
    nextCursor: data.next_cursor ?? null,
  };
}

export async function fetchPayout(payoutId: string): Promise<PayoutDetail> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/payouts/{payout_id}', {
    params: {
      path: { payout_id: payoutId },
      query: { 'expand[]': ['items', 'client'] },
    },
  });

  if (error || !data) {
    throw new Error('Failed to fetch payout');
  }

  const raw = data as unknown as Record<string, unknown>;
  const payout = mapPayoutResponse(raw);

  const rawItems = (raw.items as Record<string, unknown>[] | null) ?? [];
  const items: PayoutItem[] = rawItems.map((item) => ({
    id: item.id as string,
    payout_id: item.payout_id as string,
    client_id: item.client_id as string,
    case_id: item.case_id as string,
    external_case_ref: (item.external_case_ref as string) ?? null,
    principal_amount_millicents: Number(item.principal_amount_millicents ?? 0),
    interest_amount_millicents: Number(item.interest_amount_millicents ?? 0),
    fees_amount_millicents: Number(item.fees_amount_millicents ?? 0),
    total_amount_millicents: Number(item.total_amount_millicents ?? 0),
    created_at: (item.created_at as string) ?? null,
  }));

  return {
    ...payout,
    sender_name: (raw.sender_name as string) ?? null,
    items,
  };
}

export async function getInternalUserId(_workosUserId: string): Promise<string | null> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/users/me' as any, {});

  if (error || !data) return null;

  return ((data as any).data?.internal_user_id as string) ?? null;
}

/** The API's message, carrying its error code so callers can branch on it. */
function payoutActionError(error: unknown, fallback: string): Error & { code?: string } {
  const code = (error as { error?: { code?: string } } | undefined)?.error?.code;
  return Object.assign(new Error(platformErrorMessage(error, fallback)), { code });
}

/** Whether send or approve was refused because a case in the payout was overpaid after it was drafted. */
export function isPayoutOverpaidError(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === 'PAYOUT_CASES_OVERPAID';
}

export async function requestSendToAtlar(payoutId: string): Promise<{ status: string; payout_id: string }> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/{payout_id}/actions/send', {
    params: { path: { payout_id: payoutId } },
  });

  if (error || !data) throw payoutActionError(error, 'Failed to send to Atlar');

  return data as unknown as { status: string; payout_id: string };
}

export async function requestRetryFailedAtlarApproval(payoutId: string): Promise<{ status: string; id: string; message?: string }> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/{payout_id}/actions/retry-failed-atlar-approval' as any, {
    params: { path: { payout_id: payoutId } },
  });

  if (error || !data) {
    const msg = (error as { detail?: string })?.detail ?? 'Failed to retry approval';
    throw new Error(msg);
  }

  return data as unknown as { status: string; id: string; message?: string };
}

export async function requestApproveInAtlar(
  payoutId: string,
  opts: { overrideReason?: string; sessionId?: string } = {}
): Promise<{ status: string; payout_id: string; override_used: boolean }> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/{payout_id}/actions/approve', {
    params: {
      path: { payout_id: payoutId },
    },
    body: {
      override_reason: opts.overrideReason ?? null,
      session_id: opts.sessionId ?? null,
    } as any,
  });

  if (error || !data) throw payoutActionError(error, 'Failed to request approval');

  return data as unknown as { status: string; payout_id: string; override_used: boolean };
}

// --------------------------------------------------------------------------
// Bulk approval verification (session model) — see Platform API
// /payouts/verification/sessions* endpoints.
// --------------------------------------------------------------------------

/**
 * Platform API errors are enveloped as `{error: {code, message}}`. `detail` is the
 * FastAPI default shape, which this API does not use — reading it silently swallowed
 * every server message, so callers fell back to a generic string and lost detail the
 * server had already worked out.
 */
function platformErrorMessage(error: unknown, fallback: string): string {
  const e = error as { error?: { message?: string }; detail?: string } | undefined;
  return e?.error?.message ?? e?.detail ?? fallback;
}

export type BulkApprovalCategoryRow = {
  payout_id: string;
  client_id: string | null;
  client_name: string | null;
  amount_millicents: number | null;
  status: string | null;
  eligibility: 'eligible' | 'needs_override' | 'blocked_four_eyes' | 'blocked_status' | 'blocked_not_found';
  override_required: boolean;
  blocked_reason: string | null;
};

export async function requestCategoriseBulkApproval(payoutIds: string[]): Promise<BulkApprovalCategoryRow[]> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/verification/sessions/categorise' as any, {
    body: { payout_ids: payoutIds } as any,
  });

  if (error || !data) {
    const msg = (error as any)?.detail ?? 'Failed to categorise payouts';
    throw new Error(msg);
  }

  return ((data as any).data?.payouts ?? []) as BulkApprovalCategoryRow[];
}

/**
 * Idempotent: a live session for the same payouts is returned untouched, so a code
 * already in the operator's inbox stays valid. `reused` says which happened and
 * `sent_at` drives the resend cooldown — see replaceBulkApprovalSession for the
 * path that deliberately invalidates.
 */
export async function requestBulkApprovalSession(payoutIds: string[], email: string): Promise<BulkApprovalSessionResponse> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/verification/sessions' as any, {
    body: { payout_ids: payoutIds, email } as any,
  });

  if (error || !data) {
    const msg = platformErrorMessage(error, 'Failed to request bulk approval session');
    throw new Error(msg);
  }

  return (data as any).data;
}

export interface BulkApprovalSessionResponse {
  success: boolean;
  /** True when an existing live session was handed back rather than a new one minted. */
  reused?: boolean;
  /** True while the email is still in flight — no code has reached the inbox yet. */
  pending_send?: boolean;
  session_id?: string;
  /** When the code was emailed. Null until the worker delivers it. */
  sent_at?: string | null;
  /** When the session was requested. Cooldown basis while sent_at is still null. */
  created_at?: string | null;
  expires_at?: string | null;
  payout_count?: number;
  total_amount_millicents?: number;
  error?: string;
  message?: string;
}

/**
 * Supersedes the live session and mints a fresh code, resetting the attempt budget.
 * Rate limited server-side to one per 60s; an early call is rejected with 429.
 */
export async function replaceBulkApprovalSession(payoutIds: string[], email: string): Promise<BulkApprovalSessionResponse> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/verification/sessions/replace' as any, {
    body: { payout_ids: payoutIds, email } as any,
  });

  if (error || !data) {
    const msg = platformErrorMessage(error, 'Failed to request a new code');
    throw new Error(msg);
  }

  return (data as any).data;
}

export async function verifyBulkApprovalSession(
  sessionId: string,
  code: string
): Promise<{
  success: boolean;
  session_id?: string;
  payout_count?: number;
  error?: string;
  attempts_remaining?: number;
  message?: string;
}> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/verification/sessions/{session_id}/verify' as any, {
    params: { path: { session_id: sessionId } } as any,
    body: { code } as any,
  });

  if (error || !data) {
    throw new Error(platformErrorMessage(error, 'Verification failed'));
  }

  return (data as any).data;
}

export interface ApprovalVerificationResponse {
  success: boolean;
  /** True when an existing live verification was handed back rather than a new one minted. */
  reused?: boolean;
  /** True while the email is still in flight — no code has reached the inbox yet. */
  pending_send?: boolean;
  verification_id?: string;
  /** When the code was emailed. Null until the worker delivers it. */
  sent_at?: string | null;
  /** When the verification was requested. Cooldown basis while sent_at is still null. */
  created_at?: string | null;
  expires_at?: string | null;
  error?: string;
  message?: string;
}

/**
 * Idempotent: a live verification for the same payout is returned untouched, so a code
 * already in the operator's inbox stays valid. See replaceApprovalVerification for
 * the path that deliberately invalidates.
 */
export async function requestApprovalVerification(payoutId: string, email: string): Promise<ApprovalVerificationResponse> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/verification/request' as any, {
    body: { payout_id: payoutId, email },
  });

  if (error || !data) {
    const msg = platformErrorMessage(error, 'Verification request failed');
    throw new Error(msg);
  }

  return (data as any).data;
}

/**
 * Supersedes the live verification and mints a fresh code, resetting the attempt budget.
 * Rate limited server-side to one per 60s; an early call is rejected with 429.
 */
export async function replaceApprovalVerification(payoutId: string, email: string): Promise<ApprovalVerificationResponse> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/verification/replace' as any, {
    body: { payout_id: payoutId, email },
  });

  if (error || !data) {
    const msg = platformErrorMessage(error, 'Failed to request a new code');
    throw new Error(msg);
  }

  return (data as any).data;
}

export async function verifyApprovalCode(
  verificationId: string,
  code: string
): Promise<{ success: boolean; payout_id?: string; error?: string; attempts_remaining?: number; message?: string }> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/verification/verify' as any, {
    body: { verification_id: verificationId, code },
  });

  if (error || !data) {
    throw new Error(platformErrorMessage(error, 'Verification failed'));
  }

  return (data as any).data;
}

export async function fetchPayoutL2Assignments(payoutId: string): Promise<PayoutL2Assignment[]> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/payouts/{payout_id}/assignments', {
    params: { path: { payout_id: payoutId } },
  });

  if (error || !data) {
    throw new Error('Failed to fetch L2 assignments');
  }

  const raw = data as unknown as { data?: Record<string, unknown>[] };
  const items = raw.data ?? (Array.isArray(data) ? (data as Record<string, unknown>[]) : []);

  return items.map((a) => ({
    id: a.id as string,
    payout_id: a.payout_id as string,
    operator_id: a.operator_id as string,
    assigned_user_id: a.assigned_user_id as string,
    assigned_by_user_id: a.assigned_by_user_id as string,
    active: (a.active as boolean) ?? true,
    created_at: (a.created_at as string) ?? '',
    assigned_user: a.assigned_user as PayoutL2Assignment['assigned_user'],
  }));
}

export interface L2Approver {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

export async function fetchL2Approvers(): Promise<L2Approver[]> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/payouts/approvers', {});

  if (error || !data) {
    throw new Error('Failed to fetch L2 approvers');
  }

  const raw = data as unknown as { data?: Record<string, unknown>[] };
  const items = raw.data ?? (Array.isArray(data) ? (data as Record<string, unknown>[]) : []);

  return items.map((a) => ({
    id: (a.user_id as string) ?? (a.id as string),
    first_name: (a.first_name as string) ?? (a.name as string)?.split(' ')[0] ?? null,
    last_name: (a.last_name as string) ?? (a.name as string)?.split(' ').slice(1).join(' ') ?? null,
    email: a.email as string,
  }));
}

export async function assignL2Approvers(payoutId: string, userIds: string[]): Promise<{ assigned_count: number }> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/{payout_id}/assignments', {
    params: { path: { payout_id: payoutId } },
    body: { user_ids: userIds },
  });

  if (error || !data) {
    const msg = (error as { detail?: string })?.detail ?? 'Failed to assign L2 approvers';
    throw new Error(msg);
  }

  return data as unknown as { assigned_count: number };
}

export async function notifyL2Approvers(
  payoutId: string,
  mode: 'all_l2' | 'selected',
  recipientUserIds?: string[]
): Promise<{ status: string; message: string }> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/{payout_id}/notifications', {
    params: {
      path: { payout_id: payoutId },
    },
    body: {
      mode,
      recipient_user_ids: mode === 'selected' ? (recipientUserIds ?? []) : null,
    },
  });

  if (error || !data) {
    const msg = (error as { detail?: string })?.detail ?? 'Failed to notify L2 approvers';
    throw new Error(msg);
  }

  return data as unknown as { status: string; message: string };
}

export async function fetchPayoutAudit(payoutId: string): Promise<PayoutEvent[]> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/payouts/{payout_id}/audit', {
    params: { path: { payout_id: payoutId } },
  });

  if (error || !data) {
    throw new Error('Failed to fetch audit');
  }

  const raw = data as unknown as { data?: Record<string, unknown>[] };
  const items = raw.data ?? (Array.isArray(data) ? (data as Record<string, unknown>[]) : []);

  return items.map((e) => {
    const raw = e as Record<string, unknown>;
    const actorUserId = (raw.actor_user_id ?? raw.actor_id) as string;
    const createdAt = (raw.created_at ?? raw.timestamp) as string | undefined;
    const metadata = (raw.metadata ?? raw.details) as Record<string, unknown> | null;
    return {
      id: e.id as string,
      payout_id: e.payout_id as string,
      event_type: e.event_type as string,
      actor_user_id: actorUserId,
      actor_name: (e.actor_name as string) ?? null,
      from_status: (e.from_status as string) ?? null,
      to_status: (e.to_status as string) ?? null,
      metadata,
      created_at: createdAt ?? '',
    };
  });
}

export async function confirmPayoutReconciliation(
  payoutId: string,
  bankStatementLineId: string
): Promise<{ status: string; payout_id: string; bank_statement_line_id: string }> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/{payout_id}/actions/confirm' as any, {
    params: { path: { payout_id: payoutId } },
    body: { bank_statement_line_id: bankStatementLineId },
  });

  if (error || !data) {
    const msg = (error as { detail?: string })?.detail ?? 'Failed to confirm payout';
    throw new Error(msg);
  }

  return data as unknown as { status: string; payout_id: string; bank_statement_line_id: string };
}

export interface TriggerSchedulerResponse {
  status: string;
  message: string;
}

export async function triggerPayoutScheduler(): Promise<TriggerSchedulerResponse> {
  const { data, error } = await platformApi.POST('/platform/v1/finance/payouts/scheduler/trigger' as any, {});
  if (error) throw new Error((error as any)?.detail || 'Failed to trigger scheduler');
  return data as TriggerSchedulerResponse;
}

export async function fetchUnmatchedPayouts(params?: { cursor?: string | null; pageSize?: number }): Promise<UnmatchedPayoutsListResponse> {
  const limit = params?.pageSize ?? 100;

  const { data, error } = await platformApi.GET('/platform/v1/finance/payouts/unmatched', {
    params: {
      query: {
        limit,
        cursor: params?.cursor ?? undefined,
      },
    },
  });

  if (error || !data) {
    throw new Error('Failed to fetch unmatched payouts');
  }

  const payouts: UnmatchedPayout[] = data.data.map((r: any) => {
    const raw = r as unknown as Record<string, unknown>;
    return {
      id: raw.id as string,
      client_name: ((raw.client as Record<string, unknown>)?.display_name as string) ?? null,
      amount_millicents: Number(raw.amount_millicents),
      currency: (raw.currency as string) ?? 'EUR',
      status: raw.status as string,
      period_start: (raw.period_start as string) ?? null,
      period_end: (raw.period_end as string) ?? null,
      sent_at: (raw.sent_at as string) ?? null,
    };
  });

  return {
    payouts,
    hasMore: data.has_more,
    nextCursor: data.next_cursor ?? null,
  };
}
